import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

type Props = {
  data: number[];
  labels: string[];
  width?: number;
  height?: number;
  showValues?: boolean;
  showGrid?: boolean;
  barColor?: string;
  /** Bar color when colorByValue: (value, max) => color */
  colorByValue?: boolean;
};

const MARGIN = { top: 20, right: 24, bottom: 56, left: 48 };

/**
 * D3 bar chart with proper axes and baseline. Used for hint usage by participant, etc.
 */
export default function D3BarChart({
  data,
  labels,
  width = 640,
  height = 320,
  showValues = true,
  showGrid = true,
  barColor = "#3b82f6",
  colorByValue = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !data.length || !labels.length) return;

    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;

    const maxVal = Math.max(1, ...data.map((n) => (Number.isFinite(n) ? n : 0)));
    const yMax = maxVal * 1.1;

    const xScale = d3
      .scaleBand<string>()
      .domain(labels)
      .range([0, innerWidth])
      .padding(0.25);

    const yScale = d3.scaleLinear().domain([0, yMax]).range([innerHeight, 0]).nice();

    // Clear previous
    d3.select(containerRef.current).selectAll("*").remove();

    const svg = d3
      .select(containerRef.current)
      .append("svg")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("role", "img")
      .attr("aria-label", "Bar chart");

    const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    // Grid
    if (showGrid) {
      const yTicks = yScale.ticks(5);
      g.selectAll(".grid-line")
        .data(yTicks)
        .join("line")
        .attr("class", "grid-line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", (d) => yScale(d))
        .attr("y2", (d) => yScale(d))
        .attr("stroke", "currentColor")
        .attr("stroke-opacity", 0.15)
        .attr("stroke-width", 1);
    }

    // X-axis
    const xAxis = d3
      .axisBottom(xScale)
      .tickSizeInner(0)
      .tickSizeOuter(6)
      .tickPadding(8)
      .tickFormat((d) => (d.length > 14 ? `${d.slice(0, 13)}…` : d));

    const xAxisG = g
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(xAxis);

    xAxisG.selectAll("text").attr("font-size", 11).attr("fill", "currentColor").attr("font-weight", 500);
    xAxisG.select(".domain").attr("stroke", "currentColor").attr("stroke-opacity", 0.5).attr("stroke-width", 2);

    // Y-axis
    const yAxis = d3.axisLeft(yScale).ticks(5).tickSizeInner(-innerWidth).tickSizeOuter(0).tickPadding(8);

    const yAxisG = g.append("g").attr("class", "y-axis").call(yAxis);

    yAxisG.selectAll("text").attr("font-size", 11).attr("fill", "currentColor");
    yAxisG.select(".domain").attr("stroke", "currentColor").attr("stroke-opacity", 0.5).attr("stroke-width", 2);
    yAxisG.selectAll(".tick line").attr("stroke", "currentColor").attr("stroke-opacity", 0.15);

    // Bars
    const getColor = (value: number) => {
      if (!colorByValue) return barColor;
      return value > 0 ? "#10b981" : "#94a3b8";
    };

    g.selectAll(".bar")
      .data(data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (_, i) => xScale(labels[i]) ?? 0)
      .attr("y", (d) => yScale(Math.max(0, d)))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => innerHeight - yScale(Math.max(0, d)))
      .attr("fill", (d, i) => getColor(d))
      .attr("rx", 2);

    // Value labels on top of bars
    if (showValues) {
      g.selectAll(".bar-label")
        .data(data)
        .join("text")
        .attr("class", "bar-label")
        .attr("x", (_, i) => (xScale(labels[i]) ?? 0) + xScale.bandwidth() / 2)
        .attr("y", (d) => yScale(Math.max(0, d)) - 6)
        .attr("text-anchor", "middle")
        .attr("font-size", 12)
        .attr("font-weight", 600)
        .attr("fill", "currentColor")
        .text((d) => Math.round(d));
    }

    return () => {
      d3.select(containerRef.current).selectAll("*").remove();
    };
  }, [data, labels, width, height, showValues, showGrid, barColor, colorByValue]);

  return (
    <div
      ref={containerRef}
      className="min-h-[280px] w-full"
      style={{ color: "var(--foreground)" }}
    />
  );
}
